import { createSlice } from '@reduxjs/toolkit'
import { THEME_CONFIG } from 'configs/AppConfig'
import {
	NAV_TYPE_SIDE,
	NAV_TYPE_TOP,
	SIDE_NAV_LIGHT,
	SIDE_NAV_DARK,
	DIR_LTR,
	DIR_RTL
} from 'constants/ThemeConstant'

export const THEME_STORAGE_KEY = 'theme_config'

const TRANSIENT_KEYS = ['mobileNav', 'blankLayout']

const ALLOWED_VALUES = {
	navType: [NAV_TYPE_SIDE, NAV_TYPE_TOP],
	sideNavTheme: [SIDE_NAV_LIGHT, SIDE_NAV_DARK],
	currentTheme: ['light', 'dark'],
	direction: [DIR_LTR, DIR_RTL]
}

const loadPersistedTheme = () => {
	try {
		const raw = localStorage.getItem(THEME_STORAGE_KEY)
		if (!raw) {
			return { ...THEME_CONFIG }
		}
		const saved = JSON.parse(raw)
		if (!saved || typeof saved !== 'object') {
			return { ...THEME_CONFIG }
		}
		const next = { ...THEME_CONFIG }
		Object.keys(THEME_CONFIG).forEach((key) => {
			if (TRANSIENT_KEYS.includes(key) || saved[key] === undefined) {
				return
			}
			if (typeof saved[key] !== typeof THEME_CONFIG[key]) {
				return
			}
			if (ALLOWED_VALUES[key] && !ALLOWED_VALUES[key].includes(saved[key])) {
				return
			}
			next[key] = saved[key]
		})
		return next
	} catch (e) {
		return { ...THEME_CONFIG }
	}
}

export const persistTheme = (theme) => {
	try {
		const saved = { ...theme }
		TRANSIENT_KEYS.forEach((key) => {
			delete saved[key]
		})
		localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(saved))
	} catch (e) {
		// Ignore quota and private-mode storage failures.
	}
}

export const initialState = loadPersistedTheme()

export const themeSlice = createSlice({
	name: 'theme',
	initialState,
	reducers: {
        toggleCollapsedNav: (state, action) => {
			state.navCollapsed = action.payload
		},
        onNavStyleChange: (state, action) => {
			state.sideNavTheme = action.payload
		},
        onLocaleChange: (state, action) => {
			state.locale = action.payload
		},
        onNavTypeChange: (state, action) => {
			state.navType = action.payload
		},
        onTopNavColorChange: (state, action) => {
			state.topNavColor = `rgb(255, 255, 255)`
		},
        onHeaderNavColorChange: (state, action) => {
			state.headerNavColor = action.payload
		},
        onMobileNavToggle: (state, action) => {
			state.mobileNav = action.payload
		},
        onSwitchTheme: (state, action) => {
			state.currentTheme = action.payload
		},
        onDirectionChange: (state, action) => {
			state.direction = action.payload
		},
		onBlankLayout: (state, action) => {
			state.blankLayout = action.payload
		},
	},
})

export const { 
    toggleCollapsedNav,
    onNavStyleChange,
    onLocaleChange,
    onNavTypeChange,
    onTopNavColorChange,
    onHeaderNavColorChange,
    onMobileNavToggle,
    onSwitchTheme,
    onDirectionChange,
	onBlankLayout
} = themeSlice.actions

export default themeSlice.reducer